import {
  useGetHirerTeamMemberJobAccessAccessList,
  type GetHirerTeamMemberJobAccessListResult,
  type TeamMemberForJobAccess,
} from '@seek/hirer-team-member-job-access';
import { useTranslations } from '@vocab/react';
import { Box, Notice, Text, TextLink } from 'braid-design-system';
import React, { useEffect, useState } from 'react';

import { useAppConfig } from 'src/constants/AppConfig/AppConfigProvider';
import { isANZSite } from 'src/utils/site';

import {
  getAnzCsPhoneNumberFromSite,
  getHelpCentreUrlFromSite,
} from '../../../../../utils/getCsContactMethodMessageFromSite.ts';
import { AddTeamMemberView } from '../AddTeamMemberView';
import { TeamMemberAccessStepper } from '../TeamMemberAccessStepper';

import translations from './.vocab';

interface Props {
  onSubmitSuccess: () => void;
  onCancel: () => void;
  jobTitle?: string;
  jobId?: string;
  userName?: string;
}

export const TeamMemberAccess = ({
  onSubmitSuccess,
  onCancel,
  jobId,
  jobTitle,
  userName,
}: Props) => {
  const { t } = useTranslations(translations);
  const { site, language } = useAppConfig();

  const [hasLoadedTeamMembers, setHasLoadedTeamMembers] = useState(false);
  const [hasTeamMembers, setHasTeamMembers] = useState(true);
  const [teamMembers, setTeamMembers] = useState<TeamMemberForJobAccess[]>([]);
  const [hasServerError, setHasServerError] = useState<boolean>(false);
  const getHirerTeamMemberJobAccessAccessList =
    useGetHirerTeamMemberJobAccessAccessList();

  useEffect(() => {
    if (!hasLoadedTeamMembers) {
      getHirerTeamMemberJobAccessAccessList(
        ({
          error,
          teamMemberJobAccessList,
        }: GetHirerTeamMemberJobAccessListResult) => {
          setHasLoadedTeamMembers(true);
          setHasTeamMembers(teamMemberJobAccessList?.length > 0);
          setTeamMembers(teamMemberJobAccessList);
          if (Boolean(error) !== hasServerError) {
            setHasServerError(Boolean(error));
          }
        },
      );
    }
  });

  const csPhoneNumber = getAnzCsPhoneNumberFromSite(site);
  const teamMembersErrorMessage = isANZSite(site)
    ? t(
        "We can't show your team members right now. Refresh the page or check back later...",
        {
          TelLink: () => (
            <TextLink href={`tel:${csPhoneNumber}`}>{csPhoneNumber}</TextLink>
          ),
        },
      )
    : t(
        "We can't show your team members right now. Refresh the page or check back later - with Customer Service link",
        {
          Link: (children) => (
            <TextLink href={getHelpCentreUrlFromSite(site, language)}>
              {children}
            </TextLink>
          ),
        },
      );

  if (hasServerError) {
    return (
      <Box paddingX="large">
        <Notice tone="critical">
          <Text>{teamMembersErrorMessage}</Text>
        </Notice>
      </Box>
    );
  }

  if (!hasTeamMembers) {
    return <AddTeamMemberView />;
  }

  return (
    <TeamMemberAccessStepper
      onComplete={onSubmitSuccess}
      onCancel={onCancel}
      teamMembers={teamMembers}
      userName={userName}
      jobId={jobId}
      jobTitle={jobTitle}
    />
  );
};
